import { useReceiveToolMutation } from "@common/api/tools"
import { currentUserQuery } from "@common/api/users"
import type { DocumentPublic, ToolReturnCondition } from "@common/client"
import { UserInitials } from "@common/components/shared/user-initials"
import { Textarea } from "@common/components/ui/textarea"
import {
  ToggleGroup,
  ToggleGroupItem,
} from "@common/components/ui/toggle-group"
import { formatShortDate } from "@common/lib/date-utils"
import { handleError } from "@common/utils"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { useState } from "react"
import useCustomToast from "@/hooks/use-custom-toast"
import {
  ActionCard,
  ActionField,
  AttachFile,
  useCloseTo,
} from "@/routes/_layout/tools/-components/action-card"
import {
  RETURN_CONDITIONS,
  returnConditionLabel,
} from "@/routes/_layout/tools/-lib/labels"

export const Route = createFileRoute(
  "/_layout/tools/$toolId/loans/$loanId/receive",
)({
  component: ReceiveTool,
})

function confirmLabel(condition: ToolReturnCondition): string {
  switch (condition) {
    case "serviceable":
      return "Receive · back on shelf"
    case "damaged":
      return "Receive · out of service"
    case "quarantine":
      return "Receive · quarantine"
  }
}

function ReceiveTool(): React.ReactElement {
  const { toolId, loanId } = Route.useParams()
  const showToast = useCustomToast()
  const mutation = useReceiveToolMutation()
  const { data: me } = useQuery(currentUserQuery())
  const [condition, setCondition] = useState<ToolReturnCondition>("serviceable")
  const [note, setNote] = useState("")
  const [file, setFile] = useState<DocumentPublic | null>(null)
  const damaged = condition !== "serviceable"
  const close = useCloseTo()

  const receive = () =>
    mutation.mutate(
      {
        tool_id: toolId,
        loan_id: loanId,
        toolReceive: {
          condition,
          note: note.trim() || null,
          document_id: file?.id ?? null,
        },
      },
      {
        onSuccess: (tool) => {
          showToast("Received", `${tool.number} received.`, "success")
          close()
        },
        onError: (error) => handleError(error, showToast),
      },
    )

  return (
    <ActionCard
      title={
        me && (
          <>
            <span className="text-muted-foreground">Received by</span>
            <UserInitials user={me} />
            {me.first_name} {me.last_name}
            <span className="text-muted-foreground">
              · {formatShortDate(new Date())}
            </span>
          </>
        )
      }
      confirmLabel={confirmLabel(condition)}
      ready={!damaged || Boolean(note.trim())}
      pending={mutation.isPending}
      onCancel={close}
      onConfirm={receive}
    >
      <ToggleGroup
        type="single"
        size="sm"
        value={condition}
        onValueChange={(next) => {
          const picked = RETURN_CONDITIONS.find((value) => value === next)
          if (picked) setCondition(picked)
        }}
        className="w-max justify-start rounded-md bg-background p-0.5"
      >
        {RETURN_CONDITIONS.map((value) => (
          <ToggleGroupItem key={value} value={value}>
            {returnConditionLabel(value)}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
      <ActionField label={damaged ? "Condition (required)" : "Note"}>
        {(id) => (
          <Textarea
            dictation
            id={id}
            rows={2}
            value={note}
            placeholder={
              damaged
                ? "Describe the condition…"
                : "Anything stores should know"
            }
            className="bg-background"
            onChange={(event) => setNote(event.target.value)}
          />
        )}
      </ActionField>
      <AttachFile
        file={file}
        prompt={damaged ? "Add photo of the damage" : "Add photo or document"}
        accept="image/*,application/pdf"
        onChange={setFile}
      />
    </ActionCard>
  )
}
